import classNames from 'classnames'
import styles from './AmountField.module.css'
import { AmountFieldProps } from './types'
import { type ChangeEvent, type FocusEvent, forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { TextInput } from '../TextInput'
import { formatMoney, parseCents, sanitizeMoney } from '@betterplace/utils'
import { useForwardedRef } from '../../hooks'

function normalizeConstraint(value: number | string | undefined) {
  if (typeof value === 'undefined') return
  let result: number
  if (typeof value !== 'number') {
    result = parseFloat(value)
  } else {
    result = value
  }
  if (isNaN(result)) return
  return Math.round(result * 100)
}

const ClientAmountField = forwardRef<HTMLInputElement, AmountFieldProps & { locale?: string }>(
  (
    {
      label,
      locale = 'de',
      name,
      id,
      min: min_ = 0,
      onChange: onChange_,
      onBlur: onBlur_,
      onFocus: onFocus_,
      defaultValue,
      value,
      step,
      children,
      className,
      hideSteps,
      max: max_,
      ...props
    },
    ref
  ) => {
    const precision = useMemo(() => getPrecisionFromStep(step), [step])
    const [localValue, setLocalValue] = useState(
      sanitizeMoney(
        formatMoney(locale, value ?? defaultValue ?? '', { precision, format: '%v', thousandSeparator: false }),
        precision
      )
    )
    const max = useMemo(() => normalizeConstraint(max_), [max_])
    const min = useMemo(() => normalizeConstraint(min_), [min_])
    const [cursorPosition, setCursorPosition] = useState<number | null>(null)
    const [localRef, wrappedRef] = useForwardedRef(ref)
    const onChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const formatted = sanitizeAmount(event.target.value, locale, precision)
        const localParsed = parseCents(formatted)
        if ((typeof max !== 'undefined' && localParsed > max) || (typeof min !== 'undefined' && localParsed < min)) {
          const nextPosition = event.target.selectionStart ? event.target.selectionStart : 0
          setCursorPosition(nextPosition)
          return event.preventDefault()
        }

        setCursorPosition(event.target.selectionStart)
        let next = event.target.value
        if (!formatted.startsWith(event.target.value)) {
          next = formatted
        }
        const parsed = parseCents(event.target.value)
        if (event.target.value && !isNaN(parsed) && localParsed === parsed) {
          onChange_?.(event)
        }
        setLocalValue(next)
      },
      [locale, precision, min, max, onChange_]
    )

    const onBlur = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        const sanitized = sanitizeAmount(event.target.value, locale, precision)
        if (sanitized !== event.target.value) {
          event.target.value = sanitized
        }
        setCursorPosition(null)
        onBlur_?.(event)
      },
      [onBlur_, precision, locale]
    )

    const onFocus = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        setCursorPosition(event.target.selectionStart)
        onFocus_?.(event)
      },
      [onFocus_]
    )

    const valueProps = useMemo(
      () => (defaultValue ? { defaultValue: localValue } : { value: localValue }),
      [defaultValue, localValue]
    )

    useEffect(() => {
      if (typeof defaultValue !== 'undefined') return
      const v = value ?? defaultValue
      if (typeof value === 'undefined') {
        return setLocalValue('')
      }
      const update = sanitizeMoney(
        formatMoney(locale, v!, { precision, format: '%v', thousandSeparator: false }),
        precision
      )
      setLocalValue(update)
    }, [value, precision, defaultValue, locale])

    useEffect(() => {
      const valueLen = localValue?.length ?? 0
      const correctedPosition = (cursorPosition ?? 0) > valueLen ? valueLen : cursorPosition
      if (cursorPosition === null || !localRef.current || document.activeElement !== localRef.current) return

      localRef.current.setSelectionRange(correctedPosition, correctedPosition)
    }, [cursorPosition, localRef, localValue])

    return (
      <TextInput
        {...props}
        {...valueProps}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onChange}
        step={step}
        ref={wrappedRef}
        id={id || name}
        name={name}
        label={label}
        pattern="[0-9]*[.,]?[0-9]*"
        type="text"
        inputMode="decimal"
        className={classNames(className, { [styles.hideSteps!]: hideSteps })}
      >
        {children}
      </TextInput>
    )
  }
)

ClientAmountField.displayName = 'ClientAmountField'
export function sanitizeAmount<T extends number | string | undefined>(value: T, locale: string, precision = 2) {
  if (typeof value === 'undefined') {
    return value
  }
  if (value === null) {
    return value
  }
  const valueStr = String(value)
  const sanitizedValue = sanitizeMoney(valueStr, precision)
  const formattedValue = formatMoney(locale, sanitizedValue, { precision, format: '%v', thousandSeparator: false })
  const sane = sanitizeMoney(formattedValue, precision)
  if (!precision) {
    return sane
  }
  const separatorIndex = sane.search(/[.,]/)
  if (separatorIndex < 0) {
    return sane
  }
  const toPad = precision - (sane.length - 1 - separatorIndex)
  return sane.concat('0'.repeat(toPad))
}

export function getPrecisionFromStep(step: string | number | undefined): number | undefined {
  if (step === undefined) {
    return
  }
  const result = String(step).split('.')[1]?.length ?? 0
  return result
}
export default ClientAmountField
